import {
  FormControl, Typography, Box, Radio, Button, Paper,
} from '@mui/material';
import local from './assets/local.webp';
import transferencia from './assets/transferencia.png';
import mercadoPago from './assets/mercadopago.webp';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useState } from 'react';

/* ── Payment Option Card ── */
const PaymentOption = ({ value, checked, onChange, image, label }) => (
  <Paper
    elevation={0}
    onClick={() => onChange({ target: { value } })}
    sx={{
      border: checked ? '2px solid var(--accent)' : '1.5px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      p: 2.5,
      cursor: 'pointer',
      backgroundColor: checked ? 'var(--accent-soft)' : 'var(--bg-card)',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      position: 'relative',
      // Ancho fijo para que flex-wrap centre correctamente
      width: { xs: 130, sm: 150 },
      minWidth: { xs: 130, sm: 150 },
      '&:hover': { borderColor: 'var(--accent)', boxShadow: 'var(--shadow-sm)' },
    }}
  >
    {checked && (
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'var(--accent)' }} />
      </Box>
    )}
    <img src={image} alt={label} style={{ width: 80, height: 60, objectFit: 'contain' }} />
    <Typography sx={{
      fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem',
      color: checked ? 'var(--accent)' : 'var(--text-primary)', textAlign: 'center',
    }}>
      {label}
    </Typography>
    <Radio value={value} checked={checked} sx={{ display: 'none' }} />
  </Paper>
);

const SeccionPago = ({
  isLogin, paymentMethod, handlePaymentMethodChange,
  onBack, onNext, loading,
  pagoEnEcommerce, pagoEnTransferencia, pagoEnTienda,
}) => {
  const [paymentError, setPaymentError] = useState(false);

  const handleNextClick = () => {
    if (!paymentMethod) { setPaymentError(true); return; }
    setPaymentError(false);
    onNext();
  };

  const onChange = (e) => {
    handlePaymentMethodChange(e);
    setPaymentError(false);
  };

  return (
    <Box sx={{ mb: 2 }}>

      {/* ── Header ── */}
      <Paper elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PaymentOutlinedIcon sx={{ color: 'var(--accent)', fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
            Forma de Pago
          </Typography>
        </Box>
      </Paper>

      {/* ── Options ── */}
      <Paper elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)', p: { xs: 2, sm: 3 } }}>
        <FormControl component="fieldset" fullWidth error={paymentError}>
          {/*
            flex-wrap + justify-content: center centra automáticamente
            cualquier cantidad de opciones de pago:
              1 → centrada sola
              2 → dos centradas en fila
              3 → dos arriba + una centrada abajo
              4 → 2x2 centrado
          */}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}>
            {pagoEnTienda && (
              <PaymentOption value="local" checked={paymentMethod === "local"} onChange={onChange} image={local} label="Pago en local" />
            )}
            {pagoEnTransferencia && (
              <PaymentOption value="transferencia" checked={paymentMethod === "transferencia"} onChange={onChange} image={transferencia} label="Transferencia" />
            )}
            {pagoEnEcommerce && (
              <PaymentOption value="mercado-pago" checked={paymentMethod === "mercado-pago"} onChange={onChange} image={mercadoPago} label="Mercado Pago" />
            )}
          </Box>

          {paymentError && (
            <Typography sx={{ color: 'var(--danger)', textAlign: 'center', mt: 1.5, fontFamily: 'var(--font-body)', fontSize: '0.82rem', fontWeight: 600 }}>
              Debe seleccionar una forma de pago
            </Typography>
          )}
        </FormControl>
      </Paper>

      {/* ── Navegación ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 3 }}>
        <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 700,
            fontSize: '0.9rem', textTransform: 'none', py: 1.25,
            color: 'var(--text-secondary)', borderColor: 'var(--border)', borderWidth: '1.5px',
            '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
          }}>
          Volver
        </Button>
        <Button onClick={handleNextClick} disabled={loading} variant="contained" fullWidth endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700,
            fontSize: '0.9rem', textTransform: 'none', py: 1.25,
            backgroundColor: 'var(--accent)', boxShadow: '0 4px 12px rgba(233,69,96,0.3)',
            '&:hover': { backgroundColor: 'var(--accent-hover)', transform: 'translateY(-1px)' },
            '&:disabled': { backgroundColor: 'var(--border)', boxShadow: 'none' },
          }}>
          {loading ? 'Procesando...' : 'Continuar'}
        </Button>
      </Box>
    </Box>
  );
};

export default SeccionPago;